import { apiGet, apiPatch } from './client';
import type {
  RelatedIssuer,
  RelatedIssuerBatchRequest,
  RelatedIssuerKey,
} from '../types/RelatedIssuer';

// Server returns rows without `idKey` — the page needs a single key for the
// grid. Synthesize `issuerId|fromDate` here so callers (LopCrudPage idField)
// work uniformly. Same recipe as CapitalConsolidation / IdxComposition.
function withIdKey(
  rows: Omit<RelatedIssuer, 'idKey'>[],
): RelatedIssuer[] {
  return rows.map((r) => ({ ...r, idKey: `${r.issuerId}|${r.fromDate}` }));
}

function stripIdKey(
  rows: RelatedIssuer[],
): Omit<RelatedIssuer, 'idKey'>[] {
  return rows.map(({ idKey, ...rest }) => rest);
}

function parseIdKey(idKey: string): RelatedIssuerKey {
  const [issuerId, fromDate] = idKey.split('|');
  return { issuerId: Number(issuerId), fromDate };
}

// Tenant-scoped (X-Tenant-Id applied automatically by the client).
export async function getRelatedIssuers(): Promise<RelatedIssuer[]> {
  const rows = await apiGet<Omit<RelatedIssuer, 'idKey'>[]>(
    '/api/related-issuers',
  );
  return withIdKey(rows);
}

export async function saveRelatedIssuersBatch(
  added: RelatedIssuer[],
  modified: RelatedIssuer[],
  deletedIdKeys: string[],
): Promise<RelatedIssuer[]> {
  const body: RelatedIssuerBatchRequest = {
    added: stripIdKey(added),
    modified: stripIdKey(modified),
    deleted: deletedIdKeys.map(parseIdKey),
  };
  const fresh = await apiPatch<
    RelatedIssuerBatchRequest,
    Omit<RelatedIssuer, 'idKey'>[]
  >('/api/related-issuers/batch', body);
  return withIdKey(fresh);
}
